import dayjs from 'dayjs';
import type { KlaviyoMetricType } from '@dr-pam/common-components/Enums/KlaviyoMetricType';
import type { KlaviyoTestEventFieldRequirements } from '@dr-pam/common-components/Utils/KlaviyoTestEventMetadata';
import { getTrialAccessEnd } from '@dr-pam/common-components/Utils/SubscriptionCutoffUtils';

export type KlaviyoTestMetadataProductSubscriptionType = {
	id: string;
	name: string;
	durationInDays: number | null;
	isTrialSubscription: boolean;
};

export type KlaviyoTestMetadataProduct = {
	id: string;
	name: string;
	productSubscriptionTypes: KlaviyoTestMetadataProductSubscriptionType[];
	productProgrammes: { programmeId: string }[];
};

export type KlaviyoTestMetadataProgramme = { id: string; name: string; slug: string };
export type KlaviyoTestMetadataQuiz = { id: string; title: string; slug: string };
export type KlaviyoTestMetadataEventOccurrence = {
	id: string;
	start: string;
	title: string | null;
	event: { title: string };
};

export type KlaviyoTestMetadataResponse = {
	metrics: KlaviyoTestEventFieldRequirements[];
	products: KlaviyoTestMetadataProduct[];
	programmes: KlaviyoTestMetadataProgramme[];
	quizzes: KlaviyoTestMetadataQuiz[];
	eventOccurrences: KlaviyoTestMetadataEventOccurrence[];
};

export type KlaviyoTestFormValues = {
	metric: KlaviyoMetricType | null;
	email: string;
	uniquifyEmail: boolean;
	customerName: string;
	productId: string | null;
	productSubscriptionTypeId: string | null;
	fromProductId: string | null;
	fromProductSubscriptionTypeId: string | null;
	validFrom: Date | null;
	validTo: Date | null;
	automaticRenew: boolean;
	transitionDirection: string;
	resetUrl: string;
	audienceType: string;
	parentInterest: string;
	professionalType: string;
	signupSource: string;
	signupContextUrl: string;
	brandingType: string;
	userTimezone: string;
	paymentId: string;
	invoiceDownloadUrl: string;
	programmeId: string | null;
	eventOccurrenceId: string | null;
	quizId: string | null;
	/** Percentage (0–100) in the UI; sent to the API as a 0–1 fraction. */
	score: number | '';
	paymentAmountInCents: number | '';
	paymentCurrency: string;
	accountUrl: string;
};

export const initialValues: KlaviyoTestFormValues = {
	metric: null,
	email: '',
	uniquifyEmail: true,
	customerName: '',
	productId: null,
	productSubscriptionTypeId: null,
	fromProductId: null,
	fromProductSubscriptionTypeId: null,
	validFrom: null,
	validTo: null,
	automaticRenew: false,
	transitionDirection: '',
	resetUrl: 'https://drpam.au/reset-password?token=test-token',
	audienceType: '',
	parentInterest: '',
	professionalType: '',
	signupSource: '',
	signupContextUrl: '',
	brandingType: '',
	userTimezone: '',
	paymentId: '',
	invoiceDownloadUrl: '',
	programmeId: null,
	eventOccurrenceId: null,
	quizId: null,
	score: 100,
	paymentAmountInCents: '',
	paymentCurrency: 'AUD',
	accountUrl: '',
};

export type ActivityLogEntry = {
	id: string;
	sentAt: Date;
	metric: KlaviyoMetricType;
	email: string;
	success: boolean;
	message: string;
};

export function findRequirements(
	metadata: KlaviyoTestMetadataResponse | null,
	metric: KlaviyoMetricType | null,
): KlaviyoTestEventFieldRequirements | undefined {
	if (!metadata || !metric) {
		return undefined;
	}
	return metadata.metrics.find((m) => m.metric === metric);
}

export function labelFor(metadata: KlaviyoTestMetadataResponse | null, metric: KlaviyoMetricType): string {
	return metadata?.metrics.find((m) => m.metric === metric)?.label ?? metric;
}

/**
 * Decides what a dependent "subscription type" field should become when its
 * parent "product" field changes: kept if it's still one of the new
 * product's subscription types, reset to `null` otherwise. Shared by the
 * `productId` -> `productSubscriptionTypeId` pair and the
 * `fromProductId` -> `fromProductSubscriptionTypeId` pair, since both
 * follow the identical rule.
 */
export function resolveSubscriptionTypeIdOnProductChange(
	products: KlaviyoTestMetadataProduct[],
	newProductId: string | null,
	currentSubscriptionTypeId: string | null,
): string | null {
	const newProduct = products.find((product) => product.id === newProductId);
	const stillValid = newProduct?.productSubscriptionTypes.some((pst) => pst.id === currentSubscriptionTypeId);
	return stillValid ? currentSubscriptionTypeId : null;
}

/**
 * Suggests a "valid to" date from a "valid from" date plus a plan's duration,
 * without ever overwriting a value the admin has already entered. Called from
 * whichever of the two fields (valid-from date, subscription-type select) the
 * admin edits last, so it needs both current values passed in explicitly
 * rather than being tied to one field's change handler.
 *
 * Returns `undefined` when there's nothing to suggest, so callers can leave
 * `validTo` untouched instead of overwriting it with `undefined`.
 */
export function computeSuggestedValidTo(
	validFrom: Date | null,
	currentValidTo: Date | null,
	durationInDays: number | null | undefined,
	isTrialSubscription: boolean | undefined,
): Date | undefined {
	if (!validFrom || !durationInDays || currentValidTo) {
		return undefined;
	}
	return isTrialSubscription
		? getTrialAccessEnd(validFrom, durationInDays)
		: dayjs(validFrom).add(durationInDays, 'days').toDate();
}
